/* eslint-disable max-statements */
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import Pagination from '../../pagination';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSelectedSection } from '../../../../redux/actions/sideBarAction';
import TableLoadingSkeleton from '../../../Utils/LoadingAnimations/TableLoadingSkeleton';
import LazyImage from '../Components/imageComponent';
import { globalEnv } from '../../../../config/baseConfig';
import { setSearchPlaceholderAction } from '../../../../redux/actions/searchBarAction';
import { HeaderItem, sortingOrder } from '../Components/common';
import { useTypedSelector } from '../../../../hooks/useTypeSelector';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { DataNotFoundAnimation } from '../../../Utils/ErrorAnimations/dataNotFoundAnim';
import { axiosInstance } from '../../../../config/axiosInstance';
import placeholder from '../../../../assets/images/placeholder.png';

export default function ReferredExtensionsTableNoReferral() {
	const queryClient = useQueryClient();
	const [page, setPage] = useState(1);
	const [sortOrder, setSortOrder] = useState(sortingOrder.DESCENDING);
	const [selectedHeader, setSelectedHeader] = useState('updatedAt');

	const { searchQuery }: { searchQuery: string } = useTypedSelector(
		(state) => state.searchbar
	);

	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(setSelectedSection('Referred Extensions Documents'));
		dispatch(setSearchPlaceholderAction('Search for Referred Extensions'));
	}, []);

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);

	useEffect(() => {
		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const { data, isError, isFetching, isLoading } = useQuery(
		['referredExtensions', page, selectedHeader, sortOrder, searchQuery],
		() => fetchReferredExtensions(page),
		{ keepPreviousData: false, staleTime: 500, refetchOnWindowFocus: false }
	);

	// Prefetch the next page!
	useEffect(() => {
		if (data?.hasMore) {
			queryClient.prefetchQuery(['referredExtensions', page + 1], () =>
				fetchReferredExtensions(page + 1)
			);
		}
	}, [data, page, queryClient]);

	async function fetchReferredExtensions(page: number) {
		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};
		const { data } = await axiosInstance.get(
			globalEnv.BASE_URL +
				`/referred-signup/get-not-referred-all?page=${page}&sortby=${selectedHeader}&order=${sortOrder}${
					searchQuery !== '' ? '&filter=' + searchQuery : ''
				}`,
			config
		);
		return data;
	}

	const TableHeaders = () => {
		return (
			<tr>
				<HeaderItem
					name="Device id"
					slugName="deviceId"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Extension"
					slugName="extensionName"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Extension user count"
					slugName="deviceIdUser"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Creation date"
					slugName="createdAt"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
				<HeaderItem
					name="Update date"
					slugName="updatedAt"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
			</tr>
		);
	};

	return (
		<section>
			<div className="p-3">
				<div>
					<div className="overflow-x-auto ">
						<table className="table-auto w-full">
							{isFetching || isLoading || isError ? (
								<></>
							) : (
								<thead className="text-xs font-semibold uppercase dark:text-primary-text-dark text-primary-text-light ">
									<TableHeaders />
								</thead>
							)}
							<tbody className="text-sm divide-y divide-gray-300 dark:divide-gray-600 transition-all duration-500">
								{isLoading || isFetching || isError ? (
									<></>
								) : (
									data.referredExtensions.map((item: any) => (
										<TableData key={item._id} id={item._id} item={item} />
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
				{isFetching || isLoading ? (
					<div>
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
					</div>
				) : isError ? (
					<div className="h-full ml-0 ">
						<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
							<div className="mx-auto my-auto self-center">
								<div className="min-h-16">
									<DataNotFoundAnimation height={dimensions.height / 1.5} />
								</div>
								<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
									{"Couldn't find the data you're looking for!"}
								</h3>
							</div>
						</div>
					</div>
				) : (
					<Pagination
						itemsPerPage={data.meta.limit}
						totalCount={data.meta.total}
						totalPages={data.meta.pages}
						currentPage={page}
						setPage={setPage}
					/>
				)}
			</div>

			{/* <ReactQueryDevtools initialIsOpen /> */}
		</section>
	);
}

type tableDateType = {
	id: string;
	item: any;
};

const TableData = ({ id, item }: tableDateType) => {
	const [random, setRandom] = useState(Math.floor(Math.random() * 101));

	const navigate = useNavigate();
	return (
		<tr>
			<td className="p-2 whitespace-nowrap">
				<div className="text-left font-medium dark:text-gray-400">
					{item.deviceId != null ? item.deviceId : 'N/A'}
				</div>
			</td>
			<td className="col-span-1 p-2 break-all w-60">
				<div className="flex items-center">
					<div className="w-10 h-10 flex justify-center flex-shrink-0 mr-2 sm:mr-3">
						<LazyImage
							photo={
								item.extension.topic.dailyImage.url_low_res != null
									? item.extension.topic.dailyImage.url_low_res
									: placeholder
							}
							style={
								'block object-cover object-center w-full aspect-[1/1] rounded-lg'
							}
						/>
					</div>
					<div className="font-medium text-gray-800 dark:text-white">
						{item.extension.name}
					</div>
				</div>
			</td>

			<td className="p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-gray-400">
					{item.extension.usersCount}
				</div>
			</td>

			<td className="p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-gray-400">
					{moment(item.createdAt).format('MM/DD/YYYY - HH:mm')}
				</div>
			</td>
			<td className="p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-gray-400">
					{moment(item.updatedAt).format('MM/DD/YYYY - HH:mm')}
				</div>
			</td>
		</tr>
	);
};
